/**
 * The number pad for every field (2026-10-24, Ricky: on the standard iPad the keyboard's numbers are
 * the second function of its top row — a swipe down for every digit). A field says how it is typed:
 *
 * - `num`: numbers alone (a test's number, a shot's, an elevation, a station). The pad, and the
 *   keyboard stays down (`inputmode="none"`): the caret is in the field, the pad types there.
 * - `mixed`: numbers mostly, letters now and then (a test's own name, "94R"). The pad, with the
 *   keyboard a key away (ABC) — and the pad still there while it is up.
 * - `text` (any other field): words. The keyboard, and the pad beside it for the numbers in them
 *   (a key on it puts the keyboard away).
 * - `off`: neither.
 *
 * In a sheet (or a page's form card) the pad is part of it: the sheet moves aside and the pad comes
 * out of its side, level with the field. Over the plan it opens where it was last put, if that covers nothing being typed,
 * else against the side away from it. Carried by its grip anywhere, it is put back there next time.
 * Only where fingers type (an iPad, a phone): a Mac's keyboard has its numbers.
 */
import { app, updateSettings } from '../app/state.ts';
import { trackGlass } from './glass.ts';
import { createNumpad, spotFor, type Numpad, type PadExtra, type PadKey, type PadSpot, type Rect, type Vec } from './numpad.ts';

/** A field the pad types into: a text field, or a field of rich text (a label's words: ui/richtext.ts). */
type Field = HTMLInputElement | HTMLTextAreaElement | HTMLElement;
const isText = (el: Field): el is HTMLInputElement | HTMLTextAreaElement => el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
export type PadKind = 'num' | 'mixed' | 'text' | 'off';

/** Whether this device types with fingers (the pad is offered), or a test says so (`data-pad="always"` on the page). */
export function padAvailable(): boolean {
  return document.documentElement.dataset.pad === 'always' || matchMedia('(pointer: coarse)').matches;
}
/**
 * A field typed with the pad: how (`kind`), the key beside Return (`key`: a station's '+', a signed
 * number's '±', a '-'), and whether a point may be typed (`point`; a count, no). A number field's
 * keyboard stays down: its mode is kept, for the keyboard brought up from the pad.
 */
export function padField<T extends Field>(el: T, kind: PadKind, opts: { key?: '+' | '±' | '-'; point?: boolean } = {}): T {
  el.dataset.pad = kind;
  if (opts.key) el.dataset.padKey = opts.key;
  if (opts.point !== undefined) el.dataset.padPoint = String(opts.point);
  if ((kind === 'num' || kind === 'mixed') && padAvailable()) {
    el.dataset.padMode = el.inputMode && el.inputMode !== 'none' ? el.inputMode : 'text';
    el.inputMode = 'none';
  }
  return el;
}

/** What the screen keeps the pad off over the plan (the controls at the top, the tools): set by the screen showing the plan. */
let keepOut: (() => Rect[]) | null = null;
export function padKeepOut(fn: (() => Rect[]) | null): void {
  keepOut = fn;
}

let pad: Numpad | null = null;
let target: Field | null = null;
let shifted: HTMLElement | null = null;

const isField = (el: unknown): el is Field =>
  (el instanceof HTMLInputElement && ['text', 'search', 'tel', 'url', 'email'].includes(el.type)) || el instanceof HTMLTextAreaElement || (el instanceof HTMLElement && el.isContentEditable && el.getAttribute('role') === 'textbox');

interface Spec {
  kind: PadKind;
  extra: PadExtra;
  point: boolean;
}
function specOf(el: Field): Spec | null {
  if (isText(el) && (el.readOnly || el.disabled)) return null;
  const mode = el.dataset.padMode ?? el.inputMode;
  const kind = (el.dataset.pad as PadKind | undefined) ?? (/^(numeric|decimal|tel)$/.test(mode) ? 'num' : 'text');
  if (kind === 'off') return null;
  const key = el.dataset.padKey;
  const extra: PadExtra = key === '+' ? 'plus' : key === '±' ? 'sign' : key === '-' ? 'minus' : kind === 'num' ? 'clear' : 'abc';
  const point = el.dataset.padPoint ? el.dataset.padPoint === 'true' : mode !== 'numeric';
  return { kind, extra, point };
}
const bounds = (): Rect => {
  const vv = window.visualViewport;
  return vv ? { x: vv.offsetLeft, y: vv.offsetTop, w: vv.width, h: vv.height } : { x: 0, y: 0, w: innerWidth, h: innerHeight };
};
/** The keyboard is up (the visible part of the page well short of the window). */
const keyboardUp = (): boolean => {
  const vv = window.visualViewport;
  return !!vv && vv.height < innerHeight - 120;
};
/** The fields that come after this one where it is (its sheet, its callout), in order. */
function fieldsAfter(el: Field): Field[] {
  const host = el.closest('dialog, .callout-inplace, .side') ?? document.body;
  const all = [...host.querySelectorAll<HTMLElement>('input, textarea, [role="textbox"][contenteditable]')].filter((f): f is Field => isField(f) && !!specOf(f) && f.offsetParent !== null && !f.hidden);
  return all.slice(all.indexOf(el) + 1);
}

/** The pad follows the field the caret is in: from here on, for every field of the app. */
export function installFieldPad(): void {
  if (pad) return;
  pad = createNumpad({ label: 'Number pad', key: (k) => type(k), enter: () => enter(), abc: () => abc(), moved: (s) => remember(s) });
  // (CSS glass: the pad moves between a sheet and the page, and a drawn glass's canvas is never moved — ui/glass.ts)
  trackGlass(pad.el, { gpu: false });
  document.addEventListener('focusin', (e) => {
    if (isField(e.target)) attach(e.target);
  }, true);
  document.addEventListener('focusout', () => {
    // (the caret gone from every field — not into the next one, nor kept by a key of the pad)
    setTimeout(() => {
      if (target && document.activeElement !== target && !isField(document.activeElement)) detach();
    });
  }, true);
  // a field taken away, or hidden, with the caret in it (a callout's typing ended): the pad goes too —
  // looked for at the next touch anywhere, and a moment after every key of the pad
  document.addEventListener('pointerdown', () => gone(), true);
  // what is typed may change the field's size (a callout's box): the pad keeps clear of it
  document.addEventListener('input', () => {
    if (target && pad?.shown && !target.closest('dialog')) place(true, true);
  }, true);
  const relayout = () => {
    if (!target || !pad?.shown) return;
    layout();
    place(true);
  };
  window.visualViewport?.addEventListener('resize', relayout);
  window.visualViewport?.addEventListener('scroll', relayout);
  addEventListener('resize', relayout);
}

/** The pad's field is no longer there to type into (taken away, hidden): the pad goes — said by a screen as it is left. */
export function padGone(): void {
  gone();
}
function gone(): void {
  if (target && (!target.isConnected || target.offsetParent === null)) detach();
}
/** The pad for a field now (a sheet just opened with it — the caret may not have arrived yet). */
export function padFor(el: Field): void {
  if (pad) attach(el);
}

function attach(el: Field): void {
  if (!pad) return;
  const spec = specOf(el);
  if (!spec || !padAvailable()) return detach();
  if (target && target !== el) target.classList.remove('pad-target');
  target = el;
  el.classList.add('pad-target');
  const host = el.closest('dialog') ?? document.body;
  if (pad.el.parentElement !== host) {
    const was = pad.shown;
    if (was) pad.hide();
    host.append(pad.el);
    // a sheet closing takes its pad with it
    const d = el.closest('dialog');
    d?.addEventListener('close', () => {
      if (target && !target.isConnected) detach();
    }, { once: true });
  }
  layout();
  place(pad.shown);
}

function detach(): void {
  if (shifted) {
    shifted.style.setProperty('--pad-dx', '0px');
    shifted.style.setProperty('--pad-dy', '0px');
    shifted = null;
  }
  if (target) {
    target.classList.remove('pad-target');
    // a field whose keyboard was put away from the pad has it again next time
    if (target.dataset.padToggled) {
      target.inputMode = target.dataset.padToggled === 'auto' ? '' : target.dataset.padToggled;
      delete target.dataset.padToggled;
    }
  }
  target = null;
  pad?.hide();
}

function layout(): void {
  if (!pad || !target) return;
  const spec = specOf(target)!;
  const hint = target.enterKeyHint;
  const next = hint === 'next' || (hint !== 'done' && hint !== 'go' && hint !== 'send' && fieldsAfter(target).length > 0);
  pad.setLayout({ extra: spec.extra, point: spec.point, enter: next ? 'next' : 'done', keyboard: keyboardUp() && target.inputMode !== 'none' });
}

/**
 * Where the pad stands. In a sheet: the sheet moves aside and the pad comes out of its side, level
 * with the field (where the screen is too narrow for both, under it). Over the plan: as `spotFor`.
 * `onlyIfCovering`: kept where it is unless it now covers what is typed.
 */
function place(animate: boolean, onlyIfCovering = false): void {
  if (!pad || !target) return;
  if (!target.isConnected || target.offsetParent === null) return detach();
  const b = bounds();
  pad.compact(false);
  let s = pad.size();
  if (b.h < s.h + 24 || b.w < 640) {
    pad.compact(true);
    s = pad.size();
  }
  // (a sheet's panel, or a page's own form card: the sign-in's)
  const panel = target.closest('dialog')?.querySelector<HTMLElement>('.panel') ?? target.closest<HTMLElement>('.card-panel');
  if (panel) {
    const pr = landed(panel);
    const dx0 = Number(panel.dataset.padDx ?? 0),
      dy0 = Number(panel.dataset.padDy ?? 0);
    const base = { x: pr.left - dx0, y: pr.top - dy0, w: pr.width, h: pr.height };
    const gap = 14;
    const fr = target.getBoundingClientRect();
    let at: Vec;
    let from: 'left' | 'below';
    // (where the sheet was put aside before: the pad coming out of it moves as it moves from there)
    const was = { x: dx0, y: dy0 };
    if (base.w + gap + s.w + 32 <= b.w) {
      // side by side, the two centred together
      const dx = Math.max(Math.round(-(gap + s.w) / 2), b.x + 16 - base.x);
      shift(panel, dx, 0);
      const fy = fr.top - dy0 + fr.height / 2 - s.h / 2;
      at = { x: base.x + dx + base.w + gap, y: Math.max(b.y + 8, Math.min(b.y + b.h - s.h - 8, Math.max(base.y, Math.min(base.y + base.h - s.h, fy)))) };
      from = 'left';
    } else {
      // under it: the sheet goes up as far as it must
      at = { x: b.x + (b.w - s.w) / 2, y: b.y + b.h - s.h - 8 };
      // (never up past the top of what can be seen: a tall sheet goes up as far as that, and the pad lies over its foot)
      shift(panel, 0, Math.min(0, Math.max(Math.round(at.y - gap - (base.y + base.h)), Math.round(b.y + 8 - base.y))));
      from = 'below';
    }
    const now = { x: Number(panel.dataset.padDx ?? 0), y: Number(panel.dataset.padDy ?? 0) };
    show(at, from, animate, { x: was.x - now.x, y: was.y - now.y });
    return;
  }
  // in the side panel (ui/side.ts): out of its inner side, its top level with the panel's — the two one
  // thing at the screen's edge, the plan beside them; it stays put as the caret goes from field to field
  // (read where the panel rests: it may be coming in)
  const sidePanel = target.closest<HTMLElement>('.side');
  // (an iPad on end too: the plan keeps a strip beside the two)
  if (sidePanel && b.w - sidePanel.offsetWidth - s.w - 40 > 120) {
    const host = (sidePanel.offsetParent ?? document.body).getBoundingClientRect();
    const pr = { left: host.left + sidePanel.offsetLeft, right: host.left + sidePanel.offsetLeft + sidePanel.offsetWidth, top: host.top + sidePanel.offsetTop };
    const left = sidePanel.classList.contains('left');
    const gap = 12;
    const y = Math.max(b.y + 8, Math.min(b.y + b.h - s.h - 8, pr.top));
    const at = { x: left ? pr.right + gap : pr.left - gap - s.w, y };
    // (already there: not a pixel's move)
    if (pad.shown && pad.at && Math.abs(pad.at.x - at.x) < 1 && Math.abs(pad.at.y - at.y) < 1) return;
    show(at, left ? 'left' : 'right', animate);
    return;
  }
  // over the plan (or a page of its own: the whole of its form — the field and what is done with it — kept clear)
  const box = (target.closest<HTMLElement>('.callout-inplace, .side, form, .card-panel') ?? target).getBoundingClientRect();
  const r: Rect = { x: box.left, y: box.top, w: box.width, h: box.height };
  const avoid = [r, ...(keepOut?.() ?? [])];
  if (onlyIfCovering && pad.at && !covers(pad.at, s, r)) return;
  const at = spotFor(s, avoid, b, kept(b), r);
  show(at, at.x > r.x ? 'right' : 'left', animate);
}

function show(at: Vec, from: 'left' | 'right' | 'below', animate: boolean, trail?: Vec): void {
  if (!pad) return;
  if (pad.shown) pad.moveTo(at, animate);
  else pad.show(at, from, trail);
}
function shift(panel: HTMLElement, dx: number, dy: number): void {
  if (shifted && shifted !== panel) {
    shifted.style.setProperty('--pad-dx', '0px');
    shifted.style.setProperty('--pad-dy', '0px');
  }
  shifted = panel;
  panel.dataset.padDx = String(dx);
  panel.dataset.padDy = String(dy);
  panel.style.setProperty('--pad-dx', `${dx}px`);
  panel.style.setProperty('--pad-dy', `${dy}px`);
}
const covers = (at: Vec, s: { w: number; h: number }, r: Rect): boolean => at.x < r.x + r.w + 12 && at.x + s.w > r.x - 12 && at.y < r.y + r.h + 12 && at.y + s.h > r.y - 12;
/** An element's rect once what it is doing (coming in, moving aside) is over: read at its animations' ends, and put back unseen. */
function landed(el: HTMLElement): DOMRect {
  const anims = el.getAnimations();
  if (!anims.length) return el.getBoundingClientRect();
  const at = anims.map((a) => a.currentTime);
  for (const a of anims) a.currentTime = Number(a.effect?.getComputedTiming().endTime ?? 0);
  const r = el.getBoundingClientRect();
  anims.forEach((a, i) => (a.currentTime = at[i] ?? 0));
  return r;
}

/** Where the pad was last put over the plan (kept as a share of the screen, so a turned iPad keeps it on the same side). */
export function kept(b: Rect): PadSpot | null {
  const k = app.state.settings.padAt;
  if (!k) return null;
  return { x: b.x + k.fx * b.w, y: b.y + k.fy * b.h, dock: k.dock };
}
function remember(s: PadSpot): void {
  // (only over the plan: in a sheet it is the sheet's)
  if (!target || target.closest('dialog')) return;
  rememberPad(s);
}
/** A pad let go over the plan: the next one, for a field or a shot, opens there. */
export function rememberPad(s: PadSpot): void {
  const b = bounds();
  void updateSettings({ padAt: { fx: (s.x - b.x) / b.w, fy: (s.y - b.y) / b.h, dock: s.dock } });
}
/** The room the pad has: what can be seen of the page. */
export function padBounds(): Rect {
  return bounds();
}

/* ------------------------------------------------------------ typing */

/** A key of the pad typed into the field, as the keyboard types: the field hears it as it hears a key (`beforeinput`, then `input`). */
function type(k: PadKey): void {
  const el = target;
  if (!el) return;
  requestAnimationFrame(gone);
  if (!isText(el)) {
    // rich text: typed as the keyboard types into it (its words take the look where they land)
    if (k === 'back') document.execCommand('delete');
    else if (k === 'clear') (document.execCommand('selectAll'), document.execCommand('delete'));
    else if (k !== 'sign') document.execCommand('insertText', false, k === 'plus' ? '+' : k === 'minus' ? '-' : k);
    return;
  }
  if (k === 'back') return erase();
  if (k === 'clear') return replace(0, el.value.length, '', 'deleteContentBackward');
  if (k === 'sign') {
    // a minus at the front, or taken away
    const v = el.value;
    return v.startsWith('-') ? replace(0, 1, '', 'deleteContentBackward') : replace(0, 0, '-', 'insertText');
  }
  const text = k === 'plus' ? '+' : k === 'minus' ? '-' : k;
  const a = el.selectionStart ?? el.value.length,
    b = el.selectionEnd ?? a;
  replace(a, b, text, 'insertText');
}
function erase(): void {
  const el = target;
  if (!el || !isText(el)) return;
  const a = el.selectionStart ?? el.value.length,
    b = el.selectionEnd ?? a;
  if (a !== b) replace(a, b, '', 'deleteContentBackward');
  else if (a > 0) replace(a - 1, a, '', 'deleteContentBackward');
}
function replace(a: number, b: number, text: string, inputType: string): void {
  const el = target;
  if (!el || !isText(el)) return;
  if (text && el.maxLength > 0 && el.value.length - (b - a) + text.length > el.maxLength) return;
  // (a field that takes the edit itself — a station's plus skipped over — says so by cancelling it)
  if (document.activeElement === el) el.setSelectionRange(a, b);
  if (!el.dispatchEvent(new InputEvent('beforeinput', { inputType, data: text || null, bubbles: true, cancelable: true }))) return;
  el.setRangeText(text, a, b, 'end');
  el.dispatchEvent(new InputEvent('input', { inputType, data: text || null, bubbles: true }));
}

/**
 * Return: what the field does with Return (a sheet's answer given, the next of a callout's fields);
 * else on to the next field, or — the last — done: the caret leaves it, and the pad goes.
 */
function enter(): void {
  const el = target;
  if (!el) return;
  const handled = !el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: true }));
  if (handled) return;
  const next = fieldsAfter(el)[0];
  if (next) {
    next.focus({ preventScroll: true });
    return;
  }
  el.blur();
  detach();
}

/** The keyboard up (letters too), or put away again: the pad stays. */
function abc(): void {
  const el = target;
  if (!el) return;
  const up = keyboardUp() && el.inputMode !== 'none';
  if (!el.dataset.padToggled) el.dataset.padToggled = el.inputMode || 'auto';
  if (up) el.inputMode = 'none';
  else el.inputMode = el.dataset.padMode && el.dataset.padMode !== 'none' ? el.dataset.padMode : 'text';
  // (Safari takes a field's new mode as it is focused: the caret goes out of it and back, at once)
  el.blur();
  el.focus({ preventScroll: true });
  setTimeout(() => {
    layout();
    place(true);
  }, 420);
}
